// Moved from `src/hooks/query/useQueryClinicalTasksBySessionId.ts` (now the
// composing delegate) with the D2 family (ARCFE-945), resolving the cohort-C
// hold per the ratified P0.5 option 1: the app composes Session-cache +
// Capture-offline state and hands this hook the `isOfflineSession` fact as a
// parameter. The hook stays ignorant of both domains — no session/capture
// import under any variant, closure stays `[@heidi/tasks]` (child design D5).
//
// The legacy toast fired from inside the `queryFn` catch, and only for
// `AxiosError` — the `onFetchError` port preserves both the per-attempt timing
// and the AxiosError-only gating; the delegate owns the copy (ARCFE-921).
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { logger } from '@heidi/runtime/logger';

import { GET_CLINICAL_TASKS_BY_SESSION_ID } from '../../contracts/keys';
import type { GetClinicalTasksResponse } from '../../contracts/types';
import { getClinicalTasks } from '../api/clinical-tasks';

interface UseQueryClinicalTasksBySessionIdOptions {
  /**
   * The app-composed offline fact (Session cache + Capture offline atoms +
   * sync queue). True disables the query — an offline-recorded session has no
   * server-side tasks to fetch yet.
   */
  isOfflineSession: boolean;
  /** App-reaction port: fired where the legacy toast fired (ARCFE-921). */
  onFetchError?: (error: AxiosError) => void;
  /**
   * Caller-side gate, ANDed with the session/offline derivation. A surface
   * that only shows the tasks in one of its states (the in-session Work
   * landing) passes `false` in the others so the panel mounting alone does
   * not fetch the collection. Defaults to `true`.
   */
  enabled?: boolean;
}

export const useQueryClinicalTasksBySessionId = (
  sessionId: string,
  {
    isOfflineSession,
    onFetchError,
    enabled = true,
  }: UseQueryClinicalTasksBySessionIdOptions
) => {
  return useQuery<GetClinicalTasksResponse>({
    queryKey: [GET_CLINICAL_TASKS_BY_SESSION_ID, { sessionId }],
    enabled: enabled && !!sessionId && !isOfflineSession,
    queryFn: async () => {
      try {
        const { data } = await getClinicalTasks({ sessionId });
        return data;
      } catch (error) {
        if (error instanceof AxiosError) {
          logger.error('Error fetching tasks:', error.response?.data);
          onFetchError?.(error);
        }
        throw error;
      }
    },
  });
};
